import { apiClient } from '@/api/client';
import { Allergen } from '@/types/allergen';
import { Paginated } from '@/types/pagination';
import { mapAllergenWireToDomain } from '../mappers/allergen';
import {
  allergenWireSchema,
  paginatedAllergenResponseSchema,
  type PaginatedAllergenResponseWire,
} from '../schemas/allergen';

/**
 * Domain-oriented API module for allergen reference data.
 * Read-only: allergens are curated reference data served by the Gateway.
 */

/**
 * Query parameters for listing allergens.
 */
export interface ListAllergensParams {
  page?: number;
  per_page?: number;
  /** When true, restrict results to the FDA "big nine" allergens. */
  is_fda_big_nine?: boolean;
}

/**
 * Map a paginated wire response to a domain Paginated<Allergen>.
 */
function mapPaginatedAllergenWireToDomain(
  wire: PaginatedAllergenResponseWire,
): Paginated<Allergen> {
  return {
    items: wire.items.map(mapAllergenWireToDomain),
    total: wire.total,
    page: wire.page,
    perPage: wire.per_page,
    totalPages: wire.total_pages,
  };
}

/**
 * Build query string from list parameters.
 */
function buildQueryString(params: ListAllergensParams): string {
  const queryParams: string[] = [];

  if (params.page !== undefined) {
    queryParams.push(`page=${params.page}`);
  }
  if (params.per_page !== undefined) {
    queryParams.push(`per_page=${params.per_page}`);
  }
  if (params.is_fda_big_nine !== undefined) {
    // Gateway coerces this param via parseInt, so send 1/0 rather than true/false.
    queryParams.push(`is_fda_big_nine=${params.is_fda_big_nine ? 1 : 0}`);
  }

  return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
}

/**
 * List allergens with optional pagination and filtering.
 *
 * @param params - Query parameters for pagination / filtering
 * @returns Paginated list of domain allergens
 */
export async function listAllergens(
  params: ListAllergensParams = {},
): Promise<Paginated<Allergen>> {
  const queryString = buildQueryString(params);
  const endpoint = `/api/v1/allergens${queryString}`;

  const wire = await apiClient.get<unknown>(endpoint);
  const validated = paginatedAllergenResponseSchema.parse(wire);
  return mapPaginatedAllergenWireToDomain(validated);
}

/**
 * Get a single allergen by slug.
 *
 * @param slug - Allergen slug
 * @returns Allergen domain model
 */
export async function getAllergen(slug: string): Promise<Allergen> {
  const wire = await apiClient.get<unknown>(`/api/v1/allergens/${slug}`);
  const validated = allergenWireSchema.parse(wire);
  return mapAllergenWireToDomain(validated);
}
